import React, { useState, useEffect, useCallback, useMemo } from 'react';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import { getApiBaseUrl } from '../../../core/config/api';
import { useRealtimeInvalidation } from '../../../shared/realtime/RealtimeProvider';
import { Batiment, Personnel, Vehicule } from '../../../shared/types/game';
import BuildingInfoTab from './BuildingDetailModal/BuildingInfoTab';
import BuildingPersonnelTab from './BuildingDetailModal/BuildingPersonnelTab';
import BuildingVehiculesTab from './BuildingDetailModal/BuildingVehiculesTab';
import BuildingCellulesTab from './BuildingDetailModal/BuildingCellulesTab';
import BuildingLitsTab from './BuildingDetailModal/BuildingLitsTab';
import BuildingCTATab from './BuildingDetailModal/BuildingCTATab';
import BuildingGererTab from './BuildingDetailModal/BuildingGererTab';
import BuildingTrameEditor from '../map/views/BuildingTrameEditor';
import { useBuildingCapacity } from './BuildingDetailModal/useBuildingCapacity';
import {
  getBuildingIconPath,
  getBuildingIconConfig,
  fetchBuildingPersonnel,
  fetchBuildingVehicules,
} from './BuildingDetailModal/buildingDetailHelpers';
// Visuel du prototype (scopé sous .oscockpit) pour la coquille de la modale.
import '../cockpit/proto.css';

type TabKey = 'info' | 'personnel' | 'planning' | 'vehicules' | 'cellules' | 'lits' | 'cta' | 'gerer';

export interface BuildingDetailModalProps {
  building: Batiment;
  address?: string;
  allBuildings?: Batiment[];
  initialTab?: TabKey;
  onClose: () => void;
  onBuildingUpdate?: (updatedBuilding: Batiment) => void;
  onBuildingDelete?: (buildingId: number | bigint) => void;
}

const BuildingDetailModal: React.FC<BuildingDetailModalProps> = ({
  building,
  address,
  allBuildings,
  initialTab,
  onClose,
  onBuildingUpdate,
  onBuildingDelete,
}) => {
  const { t } = usePageTranslation('game', 'Map');
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab ?? 'info');
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [vehicules, setVehicules] = useState<Vehicule[]>([]);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);
  const [loadingVehicules, setLoadingVehicules] = useState(false);
  const [buildingData, setBuildingData] = useState<Batiment>(building);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(building.nom);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const ctaName = useMemo(
    () => allBuildings?.find((b) => b.type === 'cta' && b.id === buildingData.cta_id)?.nom,
    [allBuildings, buildingData.cta_id]
  );

  const handleBuildingUpdate = useCallback(
    (updated: Batiment) => {
      setBuildingData(updated);
      onBuildingUpdate?.(updated);
    },
    [onBuildingUpdate]
  );

  const {
    calculatedLevel,
    totalCapacity,
    totalVehiclesCapacity,
    timeRemainingPersonnel,
    timeRemainingVehicules,
    handleStartAugmentation,
  } = useBuildingCapacity(buildingData, setBuildingData, handleBuildingUpdate);

  const handleSaveName = () => {
    handleBuildingUpdate({ ...buildingData, nom: editedName, updated_at: new Date().toISOString() });
    setIsEditingName(false);
  };

  const handleDeleteBuilding = async () => {
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/buildings/${buildingData.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Erreur serveur');
      onBuildingDelete?.(buildingData.id);
      onClose();
    } catch (err: any) {
      setDeleteError(err.message || 'Erreur lors de la suppression');
    } finally {
      setDeleteLoading(false);
    }
  };

  const reloadPersonnel = useCallback(async () => {
    setLoadingPersonnel(true);
    try {
      const list = await fetchBuildingPersonnel(buildingData.id);
      if (list) setPersonnel(list);
    } finally {
      setLoadingPersonnel(false);
    }
  }, [buildingData.id]);

  const reloadVehicules = useCallback(async () => {
    setLoadingVehicules(true);
    try {
      const list = await fetchBuildingVehicules(buildingData.id);
      if (list) setVehicules(list);
    } finally {
      setLoadingVehicules(false);
    }
  }, [buildingData.id]);

  useEffect(() => {
    reloadPersonnel();
    reloadVehicules();
  }, [reloadPersonnel, reloadVehicules]);

  // Temps réel : rafraîchit personnel + véhicules dès qu'une donnée « buildings » du joueur
  // change, quelle qu'en soit la source — recrutement/achat depuis un autre onglet ou écran,
  // finalisation de planning/augmentation ou escalade côté serveur — sans rechargement manuel.
  useRealtimeInvalidation('buildings', () => {
    void reloadPersonnel();
    void reloadVehicules();
  });

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const serviceIconPath = getBuildingIconPath(buildingData.type);
  const iconConfig = getBuildingIconConfig(buildingData.type);

  const tabs: { key: TabKey; label: string; show: boolean; count: number | null }[] = [
    { key: 'info',      label: 'Informations',    show: true,                                          count: null },
    { key: 'gerer',     label: 'Vue opérationnelle', show: buildingData.type !== 'cta',                count: null },
    { key: 'personnel', label: 'Personnel',        show: buildingData.type !== 'cta',                  count: personnel.length > 0 ? personnel.length : null },
    { key: 'planning',  label: 'Planning',         show: buildingData.type !== 'cta',                  count: null },
    { key: 'vehicules', label: 'Véhicules',        show: buildingData.type !== 'cta',                  count: vehicules.length > 0 ? vehicules.length : null },
    { key: 'cellules',  label: 'Cellules',         show: buildingData.type === 'prison' || buildingData.type === 'fdo', count: (buildingData.capacite ?? 0) > 0 ? (buildingData.capacite ?? 0) : null },
    { key: 'lits',      label: 'Chambres',         show: buildingData.type === 'hopital',              count: (buildingData.capacite ?? 0) > 0 ? (buildingData.capacite ?? 0) : null },
    { key: 'cta',       label: 'Bâtiments liés',   show: buildingData.type === 'cta',                  count: null },
  ];

  return (
    <div className="oscockpit">
      <div className="bmodal-backdrop" onClick={onClose} aria-hidden="true" />
      <section className="bmodal" role="dialog" aria-modal="true" aria-label={buildingData.nom}>
        <header className="bmodal__head">
          <div className="bhead">
            <div className="bhead__ico" style={{ ['--c']: iconConfig.backgroundColor } as React.CSSProperties}>
              <img src={serviceIconPath} alt={buildingData.type} style={{ width: 22, height: 22 }} />
            </div>
            <div className="bhead__id">
              <p className="bhead__nom">{buildingData.nom}</p>
              <p className="bhead__sub">
                {t(`contextMenu.buildingTypes.${buildingData.type}`)}
                {ctaName ? ` · rattaché à ${ctaName}` : ''}
              </p>
            </div>
            <div className="bhead__kpis">
              <span className="bkpi"><b>{calculatedLevel}</b>niveau</span>
              <span className="bkpi"><b>{personnel.length}</b>agents</span>
              {buildingData.type !== 'cta' ? <span className="bkpi"><b>{vehicules.length}</b>engins</span> : null}
              {buildingData.type !== 'cta' ? <span className="bkpi"><b>{personnel.filter((p) => p.actif).length}</b>armables</span> : null}
            </div>
            <div className="bhead__badges">
              <span className={`cbadge ${buildingData.actif ? 'cbadge--on' : 'cbadge--off'}`}>
                {buildingData.actif ? 'Actif' : 'Hors service'}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {onBuildingDelete && (
              <button className="btn btn--sm btn--danger" type="button" onClick={() => setShowDeleteConfirm(true)}>
                Démolir
              </button>
            )}
            <button className="drawer__close" type="button" onClick={onClose}>
              Fermer <kbd>Échap</kbd>
            </button>
          </div>
        </header>

        <nav className="bmodal__tabs tabs">
          {tabs.filter((tab) => tab.show).map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={activeTab === tab.key ? 'is-active' : ''}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              {tab.count !== null ? <b> {tab.count}</b> : null}
            </button>
          ))}
        </nav>

        <div className="bmodal__body scroll">

          {activeTab === 'info' && (
            <BuildingInfoTab
              building={buildingData}
              address={address}
              ctaName={ctaName}
              calculatedLevel={calculatedLevel}
              totalCapacity={totalCapacity}
              totalVehiclesCapacity={totalVehiclesCapacity}
              timeRemainingPersonnel={timeRemainingPersonnel}
              timeRemainingVehicules={timeRemainingVehicules}
              handleStartAugmentation={handleStartAugmentation}
              onBuildingUpdate={handleBuildingUpdate}
              isEditingName={isEditingName}
              editedName={editedName}
              onStartEditName={() => setIsEditingName(true)}
              onChangeEditedName={setEditedName}
              onSaveName={handleSaveName}
              onCancelEditName={() => { setEditedName(buildingData.nom); setIsEditingName(false); }}
            />
          )}

          {activeTab === 'gerer' && buildingData.type !== 'cta' && (
            <BuildingGererTab
              building={buildingData}
              personnel={personnel}
              vehicules={vehicules}
              loadingPersonnel={loadingPersonnel}
              loadingVehicules={loadingVehicules}
              onPersonnelAdded={reloadPersonnel}
              onVehiculeAdded={reloadVehicules}
            />
          )}

          {activeTab === 'personnel' && buildingData.type !== 'cta' && (
            <BuildingPersonnelTab
              personnel={personnel}
              loadingPersonnel={loadingPersonnel}
              buildingType={buildingData.type}
              buildingId={buildingData.id}
              buildingName={buildingData.nom}
              userId={buildingData.user_id}
              buildingCapacity={buildingData.capacite ?? 0}
              onPersonnelAdded={reloadPersonnel}
            />
          )}

          {activeTab === 'planning' && buildingData.type !== 'cta' && (
            <BuildingTrameEditor buildings={[{ id: buildingData.id, name: buildingData.nom }]} />
          )}

          {activeTab === 'vehicules' && buildingData.type !== 'cta' && (
            <BuildingVehiculesTab
              vehicules={vehicules}
              loadingVehicules={loadingVehicules}
              buildingType={buildingData.type}
              buildingId={buildingData.id}
              userId={buildingData.user_id}
              buildingCapacity={buildingData.capacite_vehicules ?? 0}
              allBuildings={allBuildings}
              onVehiculeAdded={reloadVehicules}
            />
          )}

          {activeTab === 'cellules' && (
            <BuildingCellulesTab
              building={buildingData}
              onIncreaseCapacity={buildingData.type === 'prison' ? () => handleStartAugmentation('personnel', 1) : undefined}
            />
          )}

          {activeTab === 'lits' && (
            <BuildingLitsTab building={buildingData} />
          )}

          {activeTab === 'cta' && buildingData.type === 'cta' && (
            <BuildingCTATab ctaBuilding={buildingData} onBuildingUpdate={handleBuildingUpdate} />
          )}

        </div>
      </section>

      {/* Modale de confirmation suppression */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4"
          onClick={() => { if (!deleteLoading) setShowDeleteConfirm(false); }}
        >
          <div
            className="bg-zinc-900 rounded-xl border border-red-500/30 shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-500/15 text-red-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Supprimer ce bâtiment ?</h3>
                <p className="text-xs text-zinc-400 mt-0.5">Cette action est irréversible.</p>
              </div>
            </div>
            <p className="text-sm text-zinc-300 mb-2">
              Le bâtiment <span className="font-semibold text-white">"{buildingData.nom}"</span> ainsi que tout son personnel et ses véhicules seront définitivement supprimés.
            </p>
            <p className="text-xs text-amber-400/80 mb-5">
              Les véhicules en intervention active bloquent la suppression.
            </p>
            {deleteError && (
              <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mb-4">
                {deleteError}
              </p>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); }}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-sm transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDeleteBuilding}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-semibold transition-colors disabled:opacity-50"
              >
                {deleteLoading ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuildingDetailModal;
